import type { NewClientApproval } from './types';
import {
  NEW_CLIENT_DRY_RUN_LINE,
  NEW_CLIENT_GROUP_LABEL,
  accessLine,
  blogApprovalLine,
  plannerLine,
  servicesLine,
  setupLine,
  unmatchedLine,
} from './newClientApprovalText';

interface NewClientApprovalCardProps {
  card: NewClientApproval;
  busy?: boolean;
  onApprove: () => void;
  onDecline: () => void;
}

/**
 * The card Emjae shows before it adds a client: what will be created, who gets access, and an
 * Approve / Decline pair. Everything is plain text (names are never markup, ids are never shown).
 * A card that cannot be approved (the website already has a client, or its Google Ads account must
 * be linked by the Ads team) has no Approve button at all and says why in the backend's own sentence.
 */
export function NewClientApprovalCard({ card, busy = false, onApprove, onDecline }: NewClientApprovalCardProps) {
  const unmatched = unmatchedLine(card.unmatchedMemberCount);
  return (
    <div
      role="group"
      aria-label={NEW_CLIENT_GROUP_LABEL}
      className="mt-2 rounded-lg border border-warm-border bg-paper px-3 py-2.5"
    >
      {card.dryRun && (
        <p
          role="status"
          className="mb-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[11px] text-ink-2"
        >
          {NEW_CLIENT_DRY_RUN_LINE}
        </p>
      )}
      <p className="font-display text-[15px] leading-tight text-ink">{card.client.name}</p>
      <p className="mt-0.5 font-mono text-[11px] text-ink-2">{card.client.website}</p>
      <ul className="mt-2 grid gap-1 text-[12px] text-ink-2">
        <li>{servicesLine(card.client.services)}</li>
        <li>{blogApprovalLine(card.client.approvalRequired)}</li>
        <li>{accessLine(card.members, card.unmatchedMemberCount)}</li>
        {unmatched && <li>{unmatched}</li>}
        <li>{setupLine(card.preview.willCreate, card.dryRun)}</li>
        <li>{plannerLine(card.dryRun)}</li>
      </ul>
      {card.notice && <p className="mt-2 text-[12px] text-ink">{card.notice}</p>}
      <div className="mt-3 flex gap-2">
        {!card.declineOnly && (
          <button
            type="button"
            disabled={busy}
            onClick={onApprove}
            className="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            Approve
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onDecline}
          className="rounded-md border border-warm-border bg-paper-2 px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
        >
          Decline
        </button>
      </div>
    </div>
  );
}
